import { FunctionHeader } from '@/components/FunctionHeader';
import { ListViewBody, ListViewLabel } from '@/components/ListView';
import {
  ReceivedInvoiceReconciliation,
  ReceivedInvoiceReconciliationStatus,
} from '@/context/services/invoice/type';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  IconApproval,
  IconCancel,
  IconPending,
  ListView,
  Single,
  Tag,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, ReactNode, useMemo } from 'react';
import {
  useCancelExclusionReconciliation,
  useExcludeReconciliation,
  useSendReconciliation,
} from './hooks';
import styles from './index.module.scss';
import { SendConfirmationModal } from './SendConfirmationModal';

const cx = classnames.bind(styles);

export type Props = {
  id: string;
  data?: ReceivedInvoiceReconciliation;
};

// "unset" has no label per AC — only the action buttons below render for that status.
const STATUS_LABEL_KEYS: Record<
  ReceivedInvoiceReconciliationStatus,
  string | undefined
> = {
  unset: undefined,
  waiting: 'received_invoice_reconciliation_status_waiting',
  sent: 'received_invoice_reconciliation_status_sent',
  excluded: 'received_invoice_reconciliation_status_excluded',
};

const STATUS_TAG_STATUS: Record<
  ReceivedInvoiceReconciliationStatus,
  'default' | 'success'
> = {
  unset: 'default',
  waiting: 'success',
  sent: 'default',
  excluded: 'default',
};

// Statuses that show only a read-only tag + auto-send note, no action buttons.
const AUTO_SEND_STATUSES: ReadonlySet<ReceivedInvoiceReconciliationStatus> =
  new Set(['waiting', 'sent']);

// Action buttons displayed per status (NA-5168 AC).
const STATUS_BUTTON_KEYS: Record<
  ReceivedInvoiceReconciliationStatus,
  readonly string[]
> = {
  unset: [
    'received_invoice_reconciliation_button_exclude',
    'received_invoice_reconciliation_button_send',
  ],
  waiting: [],
  sent: [],
  excluded: ['received_invoice_reconciliation_button_cancel_exclusion'],
};

// Per Figma, the "送信" action is the only button styled as the secondary
// (blue/success) variant — every other action button is tertiary (neutral).
const SECONDARY_BUTTON_KEYS: ReadonlySet<string> = new Set([
  'received_invoice_reconciliation_button_send',
]);

const EXCLUDE_BUTTON_KEY = 'received_invoice_reconciliation_button_exclude';
const CANCEL_EXCLUSION_BUTTON_KEY =
  'received_invoice_reconciliation_button_cancel_exclusion';

export const ReconciliationStatus: FC<Props> = memo(({ id, data }) => {
  const { t } = useTranslation();
  const leftRightRatio = useMemo(
    () => ({
      left: { value: '30%' },
      right: { value: '70%' },
    }),
    []
  );
  const {
    isModalOpen,
    isPending: isSendPending,
    openModal,
    closeModal,
    handleConfirm,
  } = useSendReconciliation(id);
  const { isPending: isExcludePending, handleAction: handleExclude } =
    useExcludeReconciliation(id);
  const {
    isPending: isCancelExclusionPending,
    handleAction: handleCancelExclusion,
  } = useCancelExclusionReconciliation(id);

  // BE always populates status when the object is present (defaults to "unset"
  // server-side); this mirrors that default since the schema doesn't mark it required.
  const statusKey = data?.status ?? 'unset';

  // Per Figma, "waiting"/"sent"/"excluded" tags each show a status icon; "unset"
  // has no tag at all. "sent"'s checkmark is colored green explicitly — it
  // doesn't follow the (gray) icon color of the tag's "default" status. A
  // switch inside useMemo (matching useStatusIcon elsewhere in this codebase)
  // rather than a Record, so only the icon for the current status is ever
  // instantiated. Computed before the `data` early-return below so this hook
  // always runs in the same order (Rules of Hooks).
  const tagIcon = useMemo((): ReactNode => {
    switch (statusKey) {
      case 'waiting':
        return <IconPending />;
      case 'sent':
        return (
          <span className={cx(styles['tagIconApproval'])}>
            <IconApproval size={12} />
          </span>
        );
      case 'excluded':
        return <IconCancel size={12} />;
      default:
        return undefined;
    }
  }, [statusKey]);

  // Hidden entirely when the BE omits the key — office hasn't enabled 突合サービス連携
  // for the requesting member, same contract as the payee-drawer ReconciliationSetting.
  if (!data) {
    return null;
  }

  const labelKey = STATUS_LABEL_KEYS[statusKey];
  const tagStatus = STATUS_TAG_STATUS[statusKey];
  const buttonKeys = STATUS_BUTTON_KEYS[statusKey];

  return (
    <FunctionHeader functionName={t('received_invoice_reconciliation')}>
      <div className={cx(styles['container'])}>
        <ListView
          leftRightRatio={leftRightRatio}
          testId='reconciliation-status-view'
        >
          <Single>
            <ListViewLabel key='left'>
              {t('received_invoice_reconciliation_status')}
            </ListViewLabel>
            <ListViewBody key='right' className={cx(styles['body'])}>
              <div className={cx(styles['row'])}>
                <div className={cx(styles['statusContainer'])}>
                  <div className={cx(styles['tagPlaceholder'])}>
                    {labelKey && (
                      <Tag
                        status={tagStatus}
                        customIcon={
                          tagIcon && (
                            <span className={cx(styles['tagIcon'])}>
                              {tagIcon}
                            </span>
                          )
                        }
                        outline={false}
                        closable={false}
                      >
                        {t(labelKey)}
                      </Tag>
                    )}
                  </div>
                  {AUTO_SEND_STATUSES.has(statusKey) && (
                    <span className={cx(styles['autoSendNote'])}>
                      {t('received_invoice_reconciliation_auto_send')}
                    </span>
                  )}
                </div>
                {buttonKeys.length > 0 && (
                  <div className={cx(styles['buttonsContainer'])}>
                    {buttonKeys.map((buttonKey) => {
                      const isSecondaryButton =
                        SECONDARY_BUTTON_KEYS.has(buttonKey);

                      if (buttonKey === EXCLUDE_BUTTON_KEY) {
                        return (
                          <ButtonV2
                            key={buttonKey}
                            isSecondary={isSecondaryButton}
                            isTertiary={!isSecondaryButton}
                            size='xs'
                            onClick={handleExclude}
                            isLoading={isExcludePending}
                            isDisabled={isExcludePending}
                          >
                            {t(buttonKey)}
                          </ButtonV2>
                        );
                      }

                      if (buttonKey === CANCEL_EXCLUSION_BUTTON_KEY) {
                        return (
                          <ButtonV2
                            key={buttonKey}
                            isSecondary={isSecondaryButton}
                            isTertiary={!isSecondaryButton}
                            size='xs'
                            onClick={handleCancelExclusion}
                            isLoading={isCancelExclusionPending}
                            isDisabled={isCancelExclusionPending}
                          >
                            {t(buttonKey)}
                          </ButtonV2>
                        );
                      }

                      return (
                        <ButtonV2
                          key={buttonKey}
                          isSecondary={isSecondaryButton}
                          isTertiary={!isSecondaryButton}
                          size='xs'
                          onClick={openModal}
                          isLoading={isSendPending}
                          isDisabled={isSendPending || isExcludePending}
                        >
                          {t(buttonKey)}
                        </ButtonV2>
                      );
                    })}
                  </div>
                )}
              </div>
            </ListViewBody>
          </Single>
        </ListView>
      </div>
      <SendConfirmationModal
        open={isModalOpen}
        onClose={closeModal}
        onConfirm={handleConfirm}
        isPending={isSendPending}
      />
    </FunctionHeader>
  );
});

ReconciliationStatus.displayName = 'ReconciliationStatus';
